import React, { useState } from 'react';
import { Modal, Checkbox, message } from 'antd';
import { Settings2, LayoutDashboard, Users, CalendarDays, MessageCircle, Megaphone, FileText, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { QUICK_LINKS, useQuickLinks } from './rightPanelData';

const ICON: Record<string, typeof Users> = {
  'sales-dashboard': LayoutDashboard, customers: Users, appointments: CalendarDays, chats: MessageCircle,
  marketing: Megaphone, documents: FileText, orders: ShoppingBag,
};
const MAX = 6;

/** Lối tắt tới các màn hay dùng trong hệ thống; mỗi người tự chọn (tối đa 6) */
export function QuickLinksWidget({ meId }: { meId: string }) {
  const navigate = useNavigate();
  const { keys, setKeys, links } = useQuickLinks(meId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(keys);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 flex justify-between items-center border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Truy cập nhanh</h3>
        <button onClick={() => { setDraft(keys); setEditing(true); }} title="Chọn lối tắt" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer">
          <Settings2 className="w-4 h-4" />
        </button>
      </div>
      {links.length === 0 ? (
        <button onClick={() => { setDraft(keys); setEditing(true); }} className="w-full px-4 py-5 text-sm text-green-600 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">+ Chọn lối tắt</button>
      ) : (
        <div className="p-2 grid grid-cols-2 gap-1">
          {links.map(l => {
            const Icon = ICON[l.key] || LayoutDashboard;
            return (
              <button key={l.key} type="button" onClick={() => navigate(l.path)} title={l.desc}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left min-w-0 cursor-pointer">
                <span className={`w-8 h-8 rounded-lg ${l.color} text-white flex items-center justify-center flex-shrink-0`}><Icon className="w-4 h-4" /></span>
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 leading-tight line-clamp-2">{l.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <Modal open={editing} title="Chọn lối tắt truy cập nhanh" okText="Lưu" cancelText="Hủy" onCancel={() => setEditing(false)}
        onOk={() => { setKeys(draft); setEditing(false); message.success('Đã cập nhật truy cập nhanh'); }} destroyOnHidden>
        <p className="text-xs text-gray-500 mb-3">Chọn tối đa {MAX} màn hình bạn hay dùng. Đã chọn {draft.length}/{MAX}.</p>
        <div className="space-y-1">
          {QUICK_LINKS.map(l => {
            const checked = draft.includes(l.key);
            return (
              <label key={l.key} className={`flex items-center gap-3 p-2 rounded-lg ${checked ? 'bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'} cursor-pointer`}>
                <Checkbox checked={checked} disabled={!checked && draft.length >= MAX}
                  onChange={e => setDraft(d => (e.target.checked ? [...d, l.key] : d.filter(k => k !== l.key)))} />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-800 dark:text-gray-200">{l.label}</span>
                  <span className="block text-xs text-gray-500">{l.desc}</span>
                </span>
              </label>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
